import React from "react";
import { ViewerSection, ViewerKeyVal } from "./BaseViewer";
import { formatEmrDateInput } from "../helpers/dataHelper";

interface EMRViewerProps {
    content: string;
}

// Very first pass: expects content to be JSON already (from your ingestion step).
// Renders key sections if present; otherwise shows a small notice.
const EMRViewer: React.FC<EMRViewerProps> = ({ content }) => {
    let data: any = null;
    try {
        data = JSON.parse(content);
    } catch {
        return (
            <div className="text-sm text-hex-gray-700">Unsupported EMR JSON. Switch to Raw.</div>
        );
    }

    // Helpers
    // const join = (parts: Array<string | undefined>) => parts.filter(Boolean).join("");
    const joinSp = (parts: Array<string | undefined>) => parts.filter(p => (p ?? "").trim().length > 0).join(" ");
    const joinComma = (parts: Array<string | undefined>) => parts.filter(p => (p ?? "").trim().length > 0).join(", ");
    // Extract Patient (FHIR Patient resource or simple object)
    const fhirPatient = data?.Patient || data?.patient || data?.patientInformation || {};
    const patientName = (() => {
        if (fhirPatient?.name && Array.isArray(fhirPatient.name) && fhirPatient.name.length) {
            const nm = fhirPatient.name[0];
            const given = Array.isArray(nm.given) ? nm.given.join(" ") : nm.given;
            return joinSp([...(nm?.prefix || []), given, nm?.family]);
        }
        return fhirPatient.name || joinSp([fhirPatient.firstName, fhirPatient.lastName]);
    })();
    const patientDOB = fhirPatient.birthDate || fhirPatient.dob || fhirPatient.dateOfBirth;
    const patientGender = fhirPatient.gender;

    const identifiers = Array.isArray(fhirPatient.identifier) ? fhirPatient.identifier : [];
    const getIdByCode = (code: string) => {
        for (const id of identifiers) {
            const coding = id?.type?.coding || [];
            if (Array.isArray(coding) && coding.some((c: any) => (c.code || "").toUpperCase() === code.toUpperCase())) {
                return id?.value;
            }
        }
        return undefined;
    };
    const mrn = getIdByCode("MR") || fhirPatient.mrn || fhirPatient.medicalRecordNumber;
    const ssn = getIdByCode("SS");
    const insuranceNumber = getIdByCode("MC");

    const telecom = Array.isArray(fhirPatient.telecom) ? fhirPatient.telecom : [];
    const phones = telecom.filter((t: any) => t.system === "phone").map((t: any) => t.value);
    const emails = telecom.filter((t: any) => t.system === "email").map((t: any) => t.value);

    const addr = Array.isArray(fhirPatient.address) ? fhirPatient.address[0] : undefined;
    const addressOneLine = addr
        ? joinComma([
            (Array.isArray(addr.line) ? addr.line.join(" ") : addr.line) || undefined,
            addr.city,
            addr.state,
            addr.postalCode,
        ])
        : undefined;

    const maritalStatus = fhirPatient?.maritalStatus?.text;
    const languages = Array.isArray(fhirPatient?.communication)
        ? fhirPatient.communication.map((c: any) => c?.language?.text || c?.language?.coding?.[0]?.display).filter(Boolean)
        : [];

    // Extract FHIR-like Bundles (Observation, Encounter, Condition, Procedure, MedicationRequest, AllergyIntolerance)
    const bundleToRows = (bundle: any) => {
        const entries = bundle?.entry || [];
        return Array.isArray(entries)
            ? entries.map((e: any) => ({ id: e.id, type: e.type, code: e.code, date: e.date, val: e.val }))
            : [];
    };
    const isPopulated = (r: any) => {
        const c = (r?.code ?? "").toString().trim();
        const v = (r?.val ?? "").toString().trim();
        const d = (r?.date ?? "").toString().trim();
        return Boolean(c || v || d);
    };
    const obs = (Array.isArray(data?.observations) ? data.observations : (data?.Observation ? bundleToRows(data.Observation) : [])).filter(isPopulated);
    const enc = (Array.isArray(data?.encounters) ? data.encounters : (data?.Encounter ? bundleToRows(data.Encounter) : [])).filter(isPopulated);
    const cond = (Array.isArray(data?.conditions) ? data.conditions : (data?.Condition ? bundleToRows(data.Condition) : [])).filter(isPopulated);
    const proc = (Array.isArray(data?.procedures) ? data.procedures : (data?.Procedure ? bundleToRows(data.Procedure) : [])).filter(isPopulated);
    const meds = (Array.isArray(data?.medications) ? data.medications : (data?.MedicationRequest ? bundleToRows(data.MedicationRequest) : [])).filter(isPopulated);
    const allergies = (Array.isArray(data?.allergies) ? data.allergies : (data?.AllergyIntolerance ? bundleToRows(data.AllergyIntolerance) : [])).filter(isPopulated);

    // Insurance Information (custom block)
    const insuranceInfo = data?.InsuranceInformation || {};

    // Operative Note (document composition summary)
    const operative = data?.OperativeNote?.entry?.[0]?.resource || {};

    return (
        <div className="flex flex-col gap-4">
            <ViewerSection title="Patient Information">
                <ViewerKeyVal label="Name" value={patientName} />
                <ViewerKeyVal label="DOB" value={formatEmrDateInput(patientDOB)} />
                <ViewerKeyVal label="Gender" value={patientGender} />
                <ViewerKeyVal label="MRN" value={mrn} />
                <ViewerKeyVal label="SSN" value={ssn} />
                <ViewerKeyVal label="Insurance Number" value={insuranceNumber} />
                <ViewerKeyVal label="Address" value={addressOneLine} />
                <ViewerKeyVal label="Phone(s)" value={phones} />
                <ViewerKeyVal label="Email(s)" value={emails} />
                <ViewerKeyVal label="Marital Status" value={maritalStatus} />
                {languages.length > 0 && <ViewerKeyVal label="Languages" value={languages} />}
            </ViewerSection>

            {insuranceInfo && Object.keys(insuranceInfo).length > 0 && (
                <ViewerSection title="Insurance Information">
                    <ViewerKeyVal label="Payer" value={insuranceInfo.payer} />
                    <ViewerKeyVal label="Plan type" value={insuranceInfo.plan_type} />
                    <ViewerKeyVal label="Member ID" value={insuranceInfo.member_id} />
                    <ViewerKeyVal label="Group number" value={insuranceInfo.group_number} />
                    {insuranceInfo.prior_authorization && (
                        <>
                            <ViewerKeyVal label="Auth number" value={insuranceInfo.prior_authorization.authorization_number} />
                            <ViewerKeyVal label="Authorized procedure" value={insuranceInfo.prior_authorization.authorized_procedure} />
                            <ViewerKeyVal label="Authorization date" value={formatEmrDateInput(insuranceInfo.prior_authorization.authorization_date)} />
                            <ViewerKeyVal label="Authorized by" value={insuranceInfo.prior_authorization.authorized_by} />
                            <ViewerKeyVal label="Expiration date" value={formatEmrDateInput(insuranceInfo.prior_authorization.expiration_date)} />
                            <ViewerKeyVal label="Notes" value={insuranceInfo.prior_authorization.notes} />
                        </>
                    )}
                </ViewerSection>
            )}

            {obs && obs.length > 0 && (
                <ViewerSection title="Observations / Vitals">

                    <div className="flex flex-col gap-2">
                        {(obs as any[]).map((o, i) => (
                            <div key={o.id || i} className="flex items-start justify-between border border-hex-gray-200 rounded px-2 py-1">
                                <div className="text-sm wrap-break-word whitespace-normal pr-3">
                                    <span className="font-semibold">{o.code}</span>
                                    {o.val ? <span className="text-hex-gray-700">: {o.val}</span> : null}
                                </div>
                                <div className="text-spacing-md text-hex-gray-500 whitespace-nowrap">{formatEmrDateInput(o.date)}</div>

                            </div>
                        ))}
                    </div>
                </ViewerSection>
            )}

            {enc && enc.length > 0 && (
                <ViewerSection title="Encounters">
                    <div className="flex flex-col gap-2">
                        {(enc as any[]).map((e, i) => (

                            <div key={e.id || i} className="flex items-start justify-between border border-hex-gray-200 rounded px-2 py-1">
                                <div className="text-sm wrap-break-word whitespace-normal pr-3">
                                    <span className="font-semibold">{e.code}</span>
                                    {e.val ? <span className="text-hex-gray-700">: {e.val}</span> : null}
                                </div>
                                <div className="text-spacing-md text-hex-gray-500 whitespace-nowrap">{formatEmrDateInput(e.date)}</div>

                            </div>
                        ))}
                    </div>
                </ViewerSection>
            )}

            {cond && cond.length > 0 && (
                <ViewerSection title="Conditions">
                    <div className="flex flex-col gap-2">
                        {(cond as any[]).map((c, i) => (

                            <div key={c.id || i} className="flex items-start justify-between border border-hex-gray-200 rounded px-2 py-1">
                                <div className="text-sm wrap-break-word whitespace-normal pr-3">
                                    <span className="font-semibold">{c.code}</span>
                                    {c.val ? <span className="text-hex-gray-700">: {c.val}</span> : null}
                                </div>
                                <div className="text-spacing-md text-hex-gray-500 whitespace-nowrap">{formatEmrDateInput(c.date)}</div>

                            </div>
                        ))}
                    </div>
                </ViewerSection>
            )}

            {proc && proc.length > 0 && (
                <ViewerSection title="Procedures">
                    <div className="flex flex-col gap-2">
                        {(proc as any[]).map((p, i) => (

                            <div key={p.id || i} className="flex items-start justify-between border border-hex-gray-200 rounded px-2 py-1">
                                <div className="text-sm wrap-break-word whitespace-normal pr-3">
                                    <span className="font-semibold">{p.code}</span>
                                    {p.val ? <span className="text-hex-gray-700">: {p.val}</span> : null}
                                </div>
                                <div className="text-spacing-md text-hex-gray-500 whitespace-nowrap">{formatEmrDateInput(p.date)}</div>

                            </div>
                        ))}
                    </div>
                </ViewerSection>
            )}

            {meds && meds.length > 0 && (
                <ViewerSection title="Medications">
                    <div className="flex flex-col gap-2">
                        {(meds as any[]).map((m, i) => (

                            <div key={m.id || i} className="flex items-start justify-between border border-hex-gray-200 rounded px-2 py-1">
                                <div className="text-sm wrap-break-word whitespace-normal pr-3">
                                    <span className="font-semibold">{m.code}</span>
                                    {m.val ? <span className="text-hex-gray-700">: {m.val}</span> : null}
                                </div>
                                <div className="text-spacing-md text-hex-gray-500 whitespace-nowrap">{formatEmrDateInput(m.date)}</div>

                            </div>
                        ))}
                    </div>
                </ViewerSection>
            )}

            {allergies && allergies.length > 0 && (
                <ViewerSection title="Allergies">
                    <div className="flex flex-col gap-2">
                        {(allergies as any[]).map((a, i) => (

                            <div key={a.id || i} className="flex items-start justify-between border border-hex-gray-200 rounded px-2 py-1">
                                <div className="text-sm wrap-break-word whitespace-normal pr-3">
                                    <span className="font-semibold">{a.code}</span>
                                    {a.val ? <span className="text-hex-gray-700">: {a.val}</span> : null}
                                </div>
                                <div className="text-spacing-md text-hex-gray-500 whitespace-nowrap">{formatEmrDateInput(a.date)}</div>

                            </div>
                        ))}
                    </div>
                </ViewerSection>
            )}

            {operative && Object.keys(operative).length > 0 && (
                <ViewerSection title="Operative Note">
                    <ViewerKeyVal label="Title" value={operative.title} />
                    <ViewerKeyVal label="Date" value={formatEmrDateInput(operative.date)} />
                    <ViewerKeyVal label="Author" value={operative.author?.[0]?.display} />
                </ViewerSection>
            )}
        </div>
    );
};

export default EMRViewer;


